import React, { useState, useEffect, useCallback } from "react";
import { ArrowLeft, Plus, Pencil, Trash2, X, ChevronRight, ChevronDown, AlertTriangle, Info } from "lucide-react";
import { eur, pct0 } from "../igualdad-data.js";
import { apiGet, apiGetAll, apiPost, apiPut, apiDelete } from "../igualdad-api.js";
import { useIgualdad } from "../igualdad-store.jsx";
import { Status, SectionHead } from "../igualdad-components.jsx";
import { navigate } from "../router.jsx";

/* ============================================================
   Talention — Módulo Igualdad · Ficha de persona
   Situaciones contractuales con sus importes por concepto, los
   conflictos y huecos que devuelve el propio endpoint, y los
   agregados derivados en solo lectura: desde F2.B2 salen de las
   situaciones y el backend rechaza cambiarlos con un 409.
   ============================================================ */

const NOTA_DERIVADO = "Se calcula a partir de las situaciones de esta persona. Edítalas para cambiarlo.";

function tramoBlanco() {
  return { fecha_inicio: "", fecha_fin: "", pct_jornada: 100, puesto_id: "", categoria_id: "", centro_id: "" };
}

function IgualdadPersonaFicha({ personaId }) {
  const { session, fireToast } = useIgualdad();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [persona, setPersona] = useState(null);
  const [situaciones, setSituaciones] = useState([]);
  const [conflictos, setConflictos] = useState([]);
  const [huecos, setHuecos] = useState([]);
  const [opts, setOpts] = useState({ puestos: [], categorias: [], centros: [], conceptos: [] });

  // Importes por situación, cargados solo al desplegarla.
  const [abiertas, setAbiertas] = useState({});
  const [importes, setImportes] = useState({});
  const [altaImporte, setAltaImporte] = useState({});

  const [modal, setModal] = useState(false);
  const [editando, setEditando] = useState(null);
  const [tramo, setTramo] = useState(tramoBlanco());
  const [guardando, setGuardando] = useState(false);
  const [formErr, setFormErr] = useState(null);

  const deriva = Boolean(persona?.deriva_de_situaciones);

  const cargar = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [p, s] = await Promise.all([
        apiGet(`/personas/${personaId}`),
        apiGet(`/personas/${personaId}/situaciones`),
      ]);
      setPersona(p.data || p);
      setSituaciones(s.data || []);
      setConflictos(s.conflictos || []);
      setHuecos(s.huecos || []);
    } catch (e) { setError(e.message || true); }
    finally { setLoading(false); }
  }, [personaId]);

  useEffect(() => { cargar(); }, [cargar]);

  useEffect(() => {
    const politicaId = session.politicaId;
    Promise.all([
      apiGetAll("/puestos", politicaId ? { politica_id: politicaId } : {}),
      apiGetAll("/categorias"),
      apiGetAll("/centros-trabajo"),
      apiGetAll("/tipos-concepto", politicaId ? { politica_id: politicaId } : {}),
    ])
      .then(([pu, ca, ce, co]) => setOpts({ puestos: pu || [], categorias: ca || [], centros: ce || [], conceptos: co || [] }))
      .catch(() => setOpts({ puestos: [], categorias: [], centros: [], conceptos: [] }));
  }, [session.politicaId]);

  async function cargarImportes(situacionId) {
    try {
      const res = await apiGet("/situacion-conceptos", { situacion_id: situacionId, per_page: 100 });
      setImportes((prev) => ({ ...prev, [situacionId]: res.data || [] }));
    } catch (e) {
      setImportes((prev) => ({ ...prev, [situacionId]: [] }));
    }
  }

  async function desplegar(situacionId) {
    const abierta = Boolean(abiertas[situacionId]);
    setAbiertas((prev) => ({ ...prev, [situacionId]: !abierta }));
    if (!abierta) await cargarImportes(situacionId);
  }

  /* ---- Situaciones ---- */

  function nuevaSituacion() {
    setEditando(null); setTramo(tramoBlanco()); setFormErr(null); setModal(true);
  }

  function editarSituacion(s) {
    setEditando(s);
    setTramo({
      fecha_inicio: s.fecha_inicio || "",
      fecha_fin: s.fecha_fin || "",
      pct_jornada: s.pct_jornada,
      puesto_id: s.puesto_id ?? "",
      categoria_id: s.categoria_id ?? "",
      centro_id: s.centro_id ?? "",
    });
    setFormErr(null); setModal(true);
  }

  function cuerpoTramo() {
    return {
      persona_id: personaId,
      fecha_inicio: tramo.fecha_inicio,
      fecha_fin: tramo.fecha_fin === "" ? null : tramo.fecha_fin,
      pct_jornada: Number(tramo.pct_jornada),
      puesto_id: tramo.puesto_id === "" ? null : Number(tramo.puesto_id),
      categoria_id: tramo.categoria_id === "" ? null : Number(tramo.categoria_id),
      centro_id: tramo.centro_id === "" ? null : Number(tramo.centro_id),
    };
  }

  async function guardarSituacion() {
    if (guardando) return;
    setGuardando(true); setFormErr(null);
    try {
      if (editando) await apiPut(`/situaciones/${editando.id}`, cuerpoTramo());
      else await apiPost("/situaciones", cuerpoTramo());
      setModal(false);
      fireToast(editando ? "Situación actualizada." : "Situación creada.");
      await cargar();
    } catch (e) {
      setFormErr(e.errors ? Object.values(e.errors).flat().join(" ") : (e.message || "No se pudo guardar."));
    } finally { setGuardando(false); }
  }

  async function borrarSituacion(s) {
    try {
      await apiDelete(`/situaciones/${s.id}`);
      fireToast("Situación eliminada.");
      await cargar();
    } catch (e) { fireToast(e.message || "No se pudo eliminar."); }
  }

  /* ---- Importes por concepto ---- */

  async function anadirImporte(situacionId) {
    const alta = altaImporte[situacionId] || {};
    if (!alta.tipo_concepto_id) { fireToast("Elige un concepto."); return; }
    try {
      await apiPost("/situacion-conceptos", {
        situacion_id: situacionId,
        tipo_concepto_id: Number(alta.tipo_concepto_id),
        importe_bruto: Number(alta.importe_bruto || 0),
      });
      setAltaImporte((prev) => ({ ...prev, [situacionId]: {} }));
      fireToast("Importe añadido.");
      // El normalizado y los agregados de la persona cambian con cada escritura.
      await Promise.all([cargarImportes(situacionId), cargar()]);
    } catch (e) { fireToast(e.message || "No se pudo añadir el importe."); }
  }

  async function borrarImporte(situacionId, importeId) {
    try {
      await apiDelete(`/situacion-conceptos/${importeId}`);
      fireToast("Importe eliminado.");
      await Promise.all([cargarImportes(situacionId), cargar()]);
    } catch (e) { fireToast(e.message || "No se pudo eliminar el importe."); }
  }

  /* ---- Render ---- */

  if (loading || error) {
    return (
      <div className="rb-page">
        <h1 className="rb-h1">Ficha de persona</h1>
        <Status loading={loading} error={error} />
      </div>
    );
  }

  if (!persona) return null;

  const nombrePuesto = (id) => opts.puestos.find((o) => o.id === id)?.nombre || "—";

  return (
    <div className="rb-page">
      <button className="rb-btn-ghost" onClick={() => navigate("/igualdad/personas")}>
        <ArrowLeft size={14} /> Personas
      </button>

      <h1 className="rb-h1" style={{ marginTop: 10 }}>{persona.nombre}</h1>
      <p className="rb-lead rb-mono">{persona.codigo}</p>

      {/* Solo lectura: la ficha muestra los agregados, no los edita. Editar la
          persona sigue en el modal de la tabla, y los tres campos derivados
          estan deshabilitados alli porque el backend los rechaza con 409. */}
      <div className="rb-card">
        <div className="rb-grid2-form">
          <div>
            <label htmlFor="f-fijo">Retribución fija anual</label>
            <output id="f-fijo" className="rb-dato">{eur(Number(persona.fijo_anual))}</output>
          </div>
          <div>
            <label htmlFor="f-variable">Retribución variable anual</label>
            <output id="f-variable" className="rb-dato">{eur(Number(persona.variable_anual))}</output>
          </div>
          <div>
            {/* Persona.jornada es una fraccion 0-1, no un porcentaje. */}
            <label htmlFor="f-jornada">Jornada</label>
            <output id="f-jornada" className="rb-dato">{pct0(Number(persona.jornada) * 100)}</output>
          </div>
        </div>
        <p className="rb-note">
          <Info size={13} />{" "}
          {deriva
            ? `${NOTA_DERIVADO} Derivado de ${situaciones.filter((s) => s.estado === "valida").length} situaciones.`
            : "Esta persona no tiene situaciones válidas: sus importes son los guardados en la ficha, editables desde la tabla de personas."}
        </p>
      </div>

      {conflictos.length ? (
        <div className="rb-card" data-testid="conflictos" style={{ marginTop: 14 }}>
          <SectionHead icon={AlertTriangle} title={`Solapamientos · ${conflictos.length}`} />
          {conflictos.map((c, i) => (
            <p key={i} className="rb-note">
              La situación #{c.a} solapa con la #{c.b} del {c.desde} al {c.hasta}. Ambas quedan fuera del cálculo
              hasta que se corrija una de las dos.
            </p>
          ))}
        </div>
      ) : null}

      {huecos.length ? (
        <div className="rb-card" data-testid="huecos" style={{ marginTop: 14 }}>
          <SectionHead icon={AlertTriangle} title={`Huecos de cobertura · ${huecos.length}`} />
          {huecos.map((h, i) => (
            <p key={i} className="rb-note">Sin situación del {h.desde} al {h.hasta}. Es un aviso: no bloquea el cierre.</p>
          ))}
        </div>
      ) : null}

      <div className="rb-card rb-card-flat" style={{ marginTop: 18 }}>
        <div style={{ padding: "14px 16px" }}>
          <SectionHead
            icon={ChevronRight}
            title={`Situaciones · ${situaciones.length}`}
            right={<button className="rb-btn" onClick={nuevaSituacion}><Plus size={14} /> Nueva situación</button>}
          />
        </div>

        <table className="rb-table">
          <thead><tr>
            <th></th><th className="rb-l">Periodo</th><th>% Jornada</th><th className="rb-l">Puesto</th>
            <th>Total declarado</th><th className="rb-l">Estado</th><th className="rb-actions-h"></th>
          </tr></thead>
          <tbody>
            {situaciones.map((s) => (
              <React.Fragment key={s.id}>
                <tr data-testid={`situacion-${s.id}`}>
                  <td>
                    <button className="rb-ico-btn" data-testid={`desplegar-${s.id}`}
                      aria-label={`Ver importes de la situación ${s.id}`} onClick={() => desplegar(s.id)}>
                      {abiertas[s.id] ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                    </button>
                  </td>
                  <td className="rb-l rb-mono">{s.fecha_inicio} — {s.fecha_fin || "vigente"}</td>
                  <td className="rb-mono">{pct0(Number(s.pct_jornada))}</td>
                  <td className="rb-l">{nombrePuesto(s.puesto_id)}</td>
                  <td className="rb-mono">{s.total_declarado == null ? "—" : eur(Number(s.total_declarado))}</td>
                  <td className="rb-l">
                    <span className="rb-chip" style={{
                      background: s.estado === "valida" ? "var(--success-bg)" : "var(--danger-bg)",
                      color: s.estado === "valida" ? "var(--success-700)" : "var(--danger-700)",
                    }}>{s.estado_label || s.estado}</span>
                  </td>
                  <td className="rb-actions">
                    <button className="rb-ico-btn" title="Editar" onClick={() => editarSituacion(s)}><Pencil size={15} /></button>
                    <button className="rb-ico-btn rb-danger" title="Eliminar" onClick={() => borrarSituacion(s)}><Trash2 size={15} /></button>
                  </td>
                </tr>

                {abiertas[s.id] ? (
                  <tr>
                    <td colSpan={7}>
                      <div data-testid={`importes-${s.id}`} className="rb-valid">
                        {(importes[s.id] || []).map((im) => (
                          <div key={im.id} className="rb-validrow rb-importe">
                            <div style={{ flex: 1, textAlign: "left" }}>
                              <b>{im.tipo_concepto?.nombre || `Concepto ${im.tipo_concepto_id}`}</b>
                              <p>
                                Bruto <span data-testid={`bruto-${im.id}`}>{eur(Number(im.importe_bruto))}</span>
                                {" → normalizado "}
                                <span data-testid={`normalizado-${im.id}`}>{eur(Number(im.importe_normalizado))}</span>
                              </p>
                            </div>
                            <button className="rb-ico-btn rb-danger" data-testid={`borrar-importe-${im.id}`}
                              aria-label={`Eliminar el importe ${im.id}`} onClick={() => borrarImporte(s.id, im.id)}>
                              <Trash2 size={14} />
                            </button>
                          </div>
                        ))}
                        {!(importes[s.id] || []).length ? (
                          <p className="rb-note">Esta situación no tiene importes cargados.</p>
                        ) : null}

                        <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8 }}>
                          <select className="rb-inp" data-testid={`nuevo-concepto-${s.id}`}
                            aria-label={`Concepto nuevo para la situación ${s.id}`}
                            value={(altaImporte[s.id] || {}).tipo_concepto_id || ""}
                            onChange={(e) => setAltaImporte((prev) => ({
                              ...prev, [s.id]: { ...(prev[s.id] || {}), tipo_concepto_id: e.target.value },
                            }))}>
                            <option value="">— Concepto —</option>
                            {opts.conceptos.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                          </select>
                          <input className="rb-inp" type="number" min="0" step="0.01"
                            data-testid={`nuevo-importe-${s.id}`}
                            aria-label={`Importe nuevo para la situación ${s.id}`}
                            placeholder="Importe bruto"
                            value={(altaImporte[s.id] || {}).importe_bruto || ""}
                            onChange={(e) => setAltaImporte((prev) => ({
                              ...prev, [s.id]: { ...(prev[s.id] || {}), importe_bruto: e.target.value },
                            }))} />
                          <button className="rb-btn-soft" data-testid={`anadir-importe-${s.id}`}
                            onClick={() => anadirImporte(s.id)}><Plus size={14} /> Añadir</button>
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : null}
              </React.Fragment>
            ))}
            {!situaciones.length ? (
              <tr><td className="rb-l rb-soft" colSpan={7}>
                Esta persona no tiene situaciones. Sus importes salen de los campos de la ficha.
              </td></tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {modal ? (
        <div className="rb-modal-bg" onClick={() => setModal(false)}>
          <div className="rb-modal" onClick={(e) => e.stopPropagation()}>
            <div className="rb-modal-h">
              <div>
                <div className="rb-modal-t">{editando ? "Editar situación" : "Nueva situación"}</div>
                <div className="rb-modal-s">{persona.nombre}</div>
              </div>
              <button className="rb-ico" onClick={() => setModal(false)}><X size={18} /></button>
            </div>
            <div className="rb-modal-b">
              <div className="rb-grid2-form">
                <div>
                  <label htmlFor="s-inicio">Fecha de inicio</label>
                  <input id="s-inicio" className="rb-inp" type="date" value={tramo.fecha_inicio}
                    onChange={(e) => setTramo({ ...tramo, fecha_inicio: e.target.value })} />
                </div>
                <div>
                  <label htmlFor="s-fin">Fecha de fin</label>
                  <input id="s-fin" className="rb-inp" type="date" value={tramo.fecha_fin}
                    onChange={(e) => setTramo({ ...tramo, fecha_fin: e.target.value })} />
                  <p className="rb-note">Vacía significa vigente hasta el fin del periodo.</p>
                </div>
                <div>
                  <label htmlFor="s-jornada">% Jornada de la situación</label>
                  <input id="s-jornada" className="rb-inp" type="number" min="0.01" max="100" step="0.01"
                    value={tramo.pct_jornada}
                    onChange={(e) => setTramo({ ...tramo, pct_jornada: e.target.value })} />
                </div>
                <div>
                  <label htmlFor="s-puesto">Puesto</label>
                  <select id="s-puesto" className="rb-inp" value={tramo.puesto_id}
                    onChange={(e) => setTramo({ ...tramo, puesto_id: e.target.value })}>
                    <option value="">— Selecciona —</option>
                    {opts.puestos.map((o) => <option key={o.id} value={o.id}>{o.nombre}</option>)}
                  </select>
                </div>
                <div>
                  <label htmlFor="s-categoria">Categoría profesional</label>
                  <select id="s-categoria" className="rb-inp" value={tramo.categoria_id}
                    onChange={(e) => setTramo({ ...tramo, categoria_id: e.target.value })}>
                    <option value="">— Selecciona —</option>
                    {opts.categorias.map((o) => <option key={o.id} value={o.id}>{o.nombre}</option>)}
                  </select>
                </div>
                <div>
                  <label htmlFor="s-centro">Centro de trabajo</label>
                  <select id="s-centro" className="rb-inp" value={tramo.centro_id}
                    onChange={(e) => setTramo({ ...tramo, centro_id: e.target.value })}>
                    <option value="">— Sin centro —</option>
                    {opts.centros.map((o) => <option key={o.id} value={o.id}>{o.nombre}</option>)}
                  </select>
                </div>
              </div>
              {formErr ? <div className="rb-formerr">{formErr}</div> : null}
            </div>
            <div className="rb-modal-f">
              <button className="rb-btn-ghost" onClick={() => setModal(false)}>Cancelar</button>
              <button className="rb-btn" disabled={guardando} onClick={guardarSituacion}>
                {guardando ? "Guardando…" : (editando ? "Guardar cambios" : "Crear situación")}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export { IgualdadPersonaFicha };
